import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import FormProgressIndicator from './components/FormProgressIndicator';
import ProblemOverviewSection from './components/ProblemOverviewSection';
import RequirementsSection from './components/RequirementsSection';
import StakeholdersSection from './components/StakeholdersSection';
import TimelineSection from './components/TimelineSection';
import FileUploadSection from './components/FileUploadSection';
import PreviewSection from './components/PreviewSection';
import CompletionChecklist from './components/CompletionChecklist';

const ProblemStatementSubmission = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    // Problem Overview
    title: '',
    category: '',
    priority: '',
    complexity: '',
    description: '',
    background: '',
    affectedPopulation: '',
    currentImpact: '',
    
    // Requirements
    objectives: [],
    constraints: [],
    expectedDeliverables: [],
    successMetrics: [],
    budgetRange: '',
    resourceAvailability: '',
    
    // Stakeholders
    primaryContact: {
      name: '',
      title: '',
      department: '',
      email: '',
      phone: '',
      availability: ''
    },
    stakeholders: [],
    communicationPreferences: [],
    meetingFrequency: '',
    preferredMeetingTime: '',
    
    // Timeline
    projectDuration: '',
    urgencyLevel: '',
    projectStartDate: '',
    projectEndDate: '',
    customDuration: '',
    milestones: [],
    seasonalConsiderations: '',
    flexibilityRequirements: '',
    
    // Files
    attachments: []
  });

  const totalSteps = 5;
  const stepTitles = ['Overview', 'Requirements', 'Stakeholders', 'Timeline', 'Review'];

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/city-official-dashboard' },
    { label: 'Problem Statements', path: '/city-official-dashboard' },
    { label: 'Submit New Problem' }
  ];

  // Auto-save functionality
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (Object.keys(formData)?.some(key => formData?.[key])) {
        handleAutoSave();
      }
    }, 30000); // Auto-save every 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [formData]);

  const handleAutoSave = async () => {
    setIsAutoSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      localStorage.setItem('problemStatementDraft', JSON.stringify(formData));
      setLastSaved(new Date());
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setIsAutoSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1: // Overview
        if (!formData?.title || formData?.title?.length < 10) {
          newErrors.title = 'Title must be at least 10 characters long';
        }
        if (!formData?.description || formData?.description?.length < 100) {
          newErrors.description = 'Description must be at least 100 characters long';
        }
        if (!formData?.category) {
          newErrors.category = 'Please select a problem category';
        }
        if (!formData?.priority) {
          newErrors.priority = 'Please select a priority level';
        }
        break;
        
      case 2: // Requirements
        if (!formData?.objectives || formData?.objectives?.length < 2) {
          newErrors.objectives = 'Please add at least 2 project objectives';
        }
        if (!formData?.expectedDeliverables || formData?.expectedDeliverables?.length === 0) {
          newErrors.expectedDeliverables = 'Please select expected deliverables';
        }
        break;
        
      case 3: // Stakeholders
        if (!formData?.primaryContact?.name) {
          newErrors.primaryContactName = 'Primary contact name is required';
        }
        if (!formData?.primaryContact?.email) {
          newErrors.primaryContactEmail = 'Primary contact email is required';
        }
        break;
        
      case 4: // Timeline
        if (!formData?.projectDuration) {
          newErrors.projectDuration = 'Please select project duration';
        }
        if (!formData?.projectStartDate) {
          newErrors.projectStartDate = 'Please select project start date';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const getCompletedSections = () => {
    const completed = [];
    
    // Overview completion
    if (formData?.title && formData?.description && formData?.category && formData?.priority) {
      completed?.push('overview');
    }
    
    // Requirements completion
    if (formData?.objectives?.length >= 2 && formData?.expectedDeliverables?.length > 0) {
      completed?.push('requirements');
    }
    
    // Stakeholders completion
    if (formData?.primaryContact?.name && formData?.primaryContact?.email) {
      completed?.push('stakeholders');
    }
    
    // Timeline completion
    if (formData?.projectDuration && formData?.projectStartDate) {
      completed?.push('timeline');
    }
    
    // Review completion (all sections completed)
    if (completed?.length === 4) {
      completed?.push('review');
    }
    
    return completed;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (step) => {
    setCurrentStep(step);
  };

  const handleSaveDraft = async () => {
    try {
      setIsAutoSaving(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      localStorage.setItem('problemStatementDraft', JSON.stringify(formData));
      setLastSaved(new Date());
      alert('Draft saved successfully!');
    } catch (error) {
      console.error('Save draft failed:', error);
      alert('Failed to save draft. Please try again.');
    } finally {
      setIsAutoSaving(false);
    }
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const handleSubmit = async () => {
    const completedSections = getCompletedSections();
    if (completedSections?.length < 4) {
      alert('Please complete all required sections before submitting.');
      return;
    }

    try {
      setIsAutoSaving(true);
      // Simulate API submission
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Clear draft from localStorage
      localStorage.removeItem('problemStatementDraft');
      
      alert('Problem statement submitted successfully! You will be redirected to your dashboard.');
      navigate('/city-official-dashboard');
    } catch (error) {
      console.error('Submission failed:', error);
      alert('Failed to submit problem statement. Please try again.');
    } finally {
      setIsAutoSaving(false);
    }
  };

  const renderCurrentSection = () => {
    if (showPreview) {
      return (
        <PreviewSection 
          data={formData}
          onEdit={(step) => {
            setShowPreview(false);
            setCurrentStep(step);
          }}
        />
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <ProblemOverviewSection
            data={formData}
            onChange={handleInputChange}
            errors={errors}
          />
        );
      case 2:
        return (
          <RequirementsSection
            data={formData}
            onChange={handleInputChange}
            errors={errors}
          />
        );
      case 3:
        return (
          <StakeholdersSection
            data={formData}
            onChange={handleInputChange}
            errors={errors}
          />
        );
      case 4:
        return (
          <TimelineSection
            data={formData}
            onChange={handleInputChange}
            errors={errors}
          />
        );
      case 5:
        return (
          <div className="space-y-6">
            <FileUploadSection
              data={formData}
              onChange={handleInputChange}
              errors={errors}
            />
            <CompletionChecklist
              data={formData}
              completedSections={getCompletedSections()}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="city-official" userName="Sarah Johnson" notifications={3} />
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Breadcrumb items={breadcrumbItems} />
        
        {/* Header Section */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Submit Problem Statement</h1>
            <p className="text-muted-foreground mt-2">
              Create a structured problem statement for student teams to work on
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {lastSaved && (
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name={isAutoSaving ? "Loader2" : "Check"} 
                      size={14} 
                      className={isAutoSaving ? "animate-spin" : "text-success"} />
                <span>
                  {isAutoSaving ? 'Saving...' : `Saved ${lastSaved?.toLocaleTimeString()}`}
                </span>
              </div>
            )}
            
            <Button
              variant="outline"
              onClick={handleSaveDraft}
              disabled={isAutoSaving}
              iconName="Save"
              iconPosition="left"
              iconSize={16}
            >
              Save Draft
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {!showPreview && (
              <FormProgressIndicator
                currentStep={currentStep}
                totalSteps={totalSteps}
                completedSections={getCompletedSections()}
              />
            )}
            
            <div className="bg-card border border-border rounded-lg p-6">
              {renderCurrentSection()}
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex space-x-3">
                {!showPreview && currentStep > 1 && (
                  <Button
                    variant="outline"
                    onClick={handlePrevious}
                    iconName="ChevronLeft"
                    iconPosition="left"
                    iconSize={16}
                  >
                    Previous
                  </Button>
                )}
                
                {showPreview && (
                  <Button
                    variant="outline"
                    onClick={() => setShowPreview(false)}
                    iconName="ArrowLeft"
                    iconPosition="left"
                    iconSize={16}
                  >
                    Back to Form
                  </Button>
                )}
              </div>
              
              <div className="flex space-x-3">
                {!showPreview && (
                  <>
                    <Button
                      variant="outline"
                      onClick={handlePreview}
                      iconName="Eye"
                      iconPosition="left"
                      iconSize={16}
                    >
                      Preview
                    </Button>
                    
                    {currentStep < totalSteps ? (
                      <Button
                        onClick={handleNext}
                        iconName="ChevronRight"
                        iconPosition="right"
                        iconSize={16}
                      >
                        Next
                      </Button>
                    ) : (
                      <Button
                        onClick={handleSubmit}
                        disabled={getCompletedSections()?.length < 4 || isAutoSaving}
                        loading={isAutoSaving}
                        iconName="Send"
                        iconPosition="left"
                        iconSize={16}
                      >
                        Submit Problem Statement
                      </Button>
                    )}
                  </>
                )}
                
                {showPreview && (
                  <Button
                    onClick={handleSubmit}
                    disabled={getCompletedSections()?.length < 4 || isAutoSaving}
                    loading={isAutoSaving}
                    iconName="Send"
                    iconPosition="left"
                    iconSize={16}
                  >
                    Submit Problem Statement
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4">
              {/* Quick Navigation */}
              {!showPreview && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="font-medium text-foreground mb-3">Quick Navigation</h3>
                  <div className="space-y-2">
                    {stepTitles?.map((title, index) => {
                      const stepNumber = index + 1;
                      const isCompleted = getCompletedSections()?.includes(
                        ['overview', 'requirements', 'stakeholders', 'timeline', 'review']?.[index]
                      );
                      const isCurrent = currentStep === stepNumber;
                      
                      return (
                        <button
                          key={stepNumber}
                          onClick={() => handleStepClick(stepNumber)}
                          className={`
                            w-full flex items-center space-x-3 p-2 rounded-lg text-left transition-micro
                            ${isCurrent 
                              ? 'bg-primary text-primary-foreground' 
                              : 'hover:bg-muted text-foreground'
                            }
                          `}
                        >
                          <div className={`
                            w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium
                            ${isCompleted 
                              ? 'bg-success text-success-foreground' 
                              : isCurrent 
                              ? 'bg-primary-foreground text-primary' 
                              : 'bg-muted text-muted-foreground'
                            }
                          `}>
                            {isCompleted ? (
                              <Icon name="Check" size={12} />
                            ) : (
                              stepNumber
                            )}
                          </div>
                          <span className="text-sm">{title}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Help & Tips */}
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="HelpCircle" size={16} className="text-accent" />
                  <h3 className="font-medium text-foreground">Tips for Success</h3>
                </div>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• Be specific about the problem and its impact</li>
                  <li>• Include measurable objectives and success criteria</li>
                  <li>• Provide clear contact information for student teams</li>
                  <li>• Set realistic timelines with buffer time</li>
                  <li>• Upload supporting documents and visual references</li>
                </ul>
              </div>
              
              {/* Contact Support */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="font-medium text-foreground mb-2">Need Help?</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Contact our support team for assistance with problem statement submission.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  iconName="MessageSquare"
                  iconPosition="left"
                  iconSize={14}
                >
                  Contact Support
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProblemStatementSubmission;